import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMyPreferences,
  updateMyPreferences,
  type UserPreferences,
} from "@/api/service/userPreferences";
import { useAuth } from "@/hooks/useAuth";

export const preferencesKey = ["preferences", "me"] as const;

/**
 * The user's preferences, including onboarding state.
 *
 * `needsOnboarding` is deliberately conservative: it is only true once we
 * have a successful response that says so. A failed or in-flight request
 * must never push someone into the wizard — an established student seeing
 * a first-run flow because of a flaky network is far worse than a new one
 * reaching the home page and setting their branch there instead.
 */
export function usePreferences() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: preferencesKey,
    queryFn: getMyPreferences,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const prefs = query.data?.data ?? null;

  return {
    ...query,
    prefs,
    needsOnboarding:
      !!user &&
      query.isSuccess &&
      !!prefs &&
      prefs.onboarding_completed_at === null,
  };
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateMyPreferences,
    onSuccess: (res) => {
      if (res.data) {
        qc.setQueryData(preferencesKey, res);
      }
      // The daily-question card on home reads straight from these.
      qc.invalidateQueries({ queryKey: ["home"] });
    },
  });
}

export type { UserPreferences };
